import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Phone, Trash2, ShieldCheck, Send, Paperclip, X, Loader2, Bot, User, Download, CheckCircle2, AlertCircle } from "lucide-react";

const REASON_LABEL = {
  activation_key: "مفتاح تفعيل",
  new_account: "حساب جديد",
  custom_pricing: "أسعار / عروض",
  financial: "معلومات مالية",
  sensitive: "معلومات حساسة",
  other: "طلب مراجعة",
};

export default function SupportMessages() {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [approve, setApprove] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [legacy, setLegacy] = useState([]);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/sessions");
      setSessions(data);
      // if no selection or selection is gone, pick first
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const loadLegacy = async () => {
    try { const { data } = await api.get("/support/messages"); setLegacy(data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); loadLegacy(); }, []); // eslint-disable-line
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); }, []); // eslint-disable-line

  const selected = useMemo(() => sessions.find(s => s.id === selectedId), [sessions, selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [selectedId, selected?.messages?.length]);

  useEffect(() => {
    if (selectedId) api.put(`/support/sessions/${selectedId}/read`).catch(()=>{});
  }, [selectedId]);

  const totalPending = sessions.filter(s => s.pending).length;
  const totalUnread = sessions.filter(s => !s.read).length;

  const readB64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result || ""); const i = s.indexOf(","); resolve(i >= 0 ? s.slice(i + 1) : s); };
    r.onerror = reject; r.readAsDataURL(file);
  });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`الملف ${f.name} أكبر من 5MB`); continue; }
        const b64 = await readB64(f);
        const { data } = await api.post("/support/upload", { name: f.name, content_type: f.type || "application/octet-stream", data_b64: b64 });
        setAttachments(prev => [...prev, data]);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const sendReply = async () => {
    if (!selected) return;
    if (!replyText.trim() && attachments.length === 0) return toast.error("اكتب رداً أو أرفق ملفاً");
    setSending(true);
    try {
      await api.post(`/support/sessions/${selected.id}/reply`, {
        text: replyText.trim(), attachments, approved: approve,
      });
      setReplyText(""); setAttachments([]); setApprove(false);
      toast.success(approve ? "تم إرسال الرد والموافقة" : "تم إرسال الرد");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    if (!confirm("حذف المحادثة نهائياً؟")) return;
    try { await api.delete(`/support/sessions/${id}`); if (selectedId === id) setSelectedId(null); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="support-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><MessageCircle className="w-5 h-5" /></div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">محادثات الدعم الذكي</h1>
          <p className="text-sm text-muted-foreground">
            محادثات الزوار من شاشة الدخول عبر المساعد الذكي.
            {totalPending > 0 && <span className="ms-2 text-amber-700 font-bold">{totalPending} بانتظار الموافقة</span>}
            {totalUnread > 0 && <span className="ms-2 text-primary font-bold">{totalUnread} غير مقروءة</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={()=>setShowLegacy(v=>!v)} data-testid="toggle-legacy-btn">
          {showLegacy ? "إخفاء الرسائل القديمة" : `الرسائل القديمة (${legacy.length})`}
        </Button>
      </div>

      {showLegacy && (
        <div className="border border-border rounded-md bg-card p-3" data-testid="legacy-panel">
          <div className="text-xs font-bold text-muted-foreground mb-2">رسائل قديمة (نموذج بسيط)</div>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {legacy.length === 0 && <div className="text-sm text-muted-foreground">لا توجد رسائل قديمة</div>}
            {legacy.map(m => (
              <div key={m.id} className="text-sm border border-border rounded p-2 bg-muted/30">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">{m.name}</span>
                  {m.phone && <><Phone className="w-3 h-3" /> {m.phone}</>}
                  <span className="ms-auto">{new Date(m.created_at).toLocaleString("ar-EG")}</span>
                </div>
                <div className="mt-1 whitespace-pre-wrap">{m.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-[320px,1fr] gap-3 border border-border rounded-md bg-card overflow-hidden" style={{minHeight: "560px"}}>
        {/* Sessions list */}
        <div className="border-e border-border overflow-y-auto max-h-[560px]" data-testid="sessions-list">
          {loading && sessions.length === 0 && <div className="p-4 text-sm text-muted-foreground">جاري التحميل...</div>}
          {!loading && sessions.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">لا توجد محادثات بعد</div>}
          {sessions.map(s => {
            const last = (s.messages || []).slice(-1)[0];
            const isActive = s.id === selectedId;
            return (
              <button key={s.id} onClick={()=>setSelectedId(s.id)}
                className={`w-full text-start p-3 border-b border-border hover:bg-muted transition ${isActive ? "bg-primary/10" : ""}`}
                data-testid={`session-item-${s.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-primary/10 grid place-items-center shrink-0"><User className="w-4 h-4 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate flex items-center gap-1">
                      {s.name}
                      {!s.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0"></span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">{s.phone || "بدون هاتف"}</div>
                  </div>
                </div>
                {s.pending && (
                  <div className="text-[10px] mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                    <AlertCircle className="w-3 h-3" /> بانتظار: {REASON_LABEL[s.pending_reason] || "مراجعة"}
                  </div>
                )}
                <div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                  {last ? `${last.role === "user" ? "" : last.role === "admin" ? "أنت: " : "🤖 "}${(last.text || "").slice(0, 90)}` : "بدون رسائل"}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">{new Date(s.updated_at || s.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</div>
              </button>
            );
          })}
        </div>

        {/* Conversation view */}
        <div className="flex flex-col max-h-[560px]" data-testid="conversation-panel">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-6">اختر محادثة من القائمة</div>
          ) : (
            <>
              <div className="border-b border-border p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center"><User className="w-5 h-5 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold flex items-center gap-2">{selected.name}
                    {selected.pending && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">بانتظار الموافقة</span>}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    {selected.phone ? <a href={`tel:${selected.phone}`} className="text-primary flex items-center gap-1"><Phone className="w-3 h-3" />{selected.phone}</a> : "بدون هاتف"}
                    {selected.pending_summary && <span className="truncate">— {selected.pending_summary}</span>}
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={()=>del(selected.id)} data-testid={`delete-session-${selected.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
                {(selected.messages || []).map((m, i) => <AdminBubble key={i} m={m} />)}
                {(!selected.messages || selected.messages.length === 0) && <div className="text-center text-sm text-muted-foreground py-8">لا توجد رسائل بعد</div>}
              </div>

              {/* Composer */}
              <div className="border-t border-border p-3 bg-background">
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {attachments.map(a => (
                      <div key={a.file_id} className="text-xs px-2 py-1 bg-muted rounded flex items-center gap-1.5">
                        <Paperclip className="w-3 h-3" /> {a.name}
                        <button onClick={()=>setAttachments(prev => prev.filter(x => x.file_id !== a.file_id))} className="text-destructive"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input type="file" ref={fileRef} onChange={onPickFiles} className="hidden" multiple data-testid="admin-file-input" />
                  <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" disabled={uploading} onClick={()=>fileRef.current?.click()} title="إرفاق" data-testid="admin-attach-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </Button>
                  <textarea value={replyText} onChange={(e)=>setReplyText(e.target.value)} rows={2}
                    onKeyDown={(e)=>{ if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendReply(); } }}
                    placeholder={selected.pending ? "اكتب ردك، وأرفق مفتاح التفعيل أو الملف المطلوب... (Ctrl+Enter للإرسال)" : "اكتب رداً..."}
                    className="flex-1 min-h-[44px] max-h-40 p-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    data-testid="admin-reply-input" />
                  <Button onClick={sendReply} disabled={sending} className="h-11 shrink-0" data-testid="admin-send-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {selected.pending && (
                  <label className="mt-2 flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={approve} onChange={(e)=>setApprove(e.target.checked)} data-testid="approve-checkbox" />
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                    <span className="text-emerald-800 font-bold">اعتماد الطلب (يزيل حالة الانتظار ويُبلّغ الزائر بالموافقة)</span>
                  </label>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminBubble({ m }) {
  const isUser = m.role === "user";
  const isAI = m.role === "ai";
  const isAdmin = m.role === "admin";
  const align = isAdmin ? "flex-row-reverse" : "";
  const bubble = isAdmin
    ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
    : isAI
      ? "bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl rounded-tl-sm"
      : "bg-background border border-border rounded-2xl rounded-tl-sm";
  const Icon = isAdmin ? ShieldCheck : (isAI ? Bot : User);
  return (
    <div className={`flex gap-2 ${align}`}>
      <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${isAdmin ? "bg-primary" : isAI ? "bg-blue-600" : "bg-primary/10"}`}>
        <Icon className={`w-4 h-4 ${isAdmin || isAI ? "text-white" : "text-primary"}`} />
      </div>
      <div className={`max-w-[80%] px-3.5 py-2 ${bubble}`}>
        <div className="text-[10px] font-bold mb-1 opacity-70">
          {isAdmin ? `أنت${m.by ? ` — ${m.by}` : ""}${m.approved ? " · موافقة" : ""}` : isAI ? "🤖 المساعد الذكي" : "الزائر"}
        </div>
        {m.text && <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>}
        {(m.attachments || []).length > 0 && (
          <div className="mt-2 space-y-1">
            {m.attachments.map(a => (
              <a key={a.file_id} href={`${API}/support/file/${a.file_id}`} target="_blank" rel="noreferrer"
                 className={`text-xs flex items-center gap-1.5 underline ${isAdmin ? "text-white/90" : "text-primary"}`}
                 data-testid={`admin-attach-link-${a.file_id}`}>
                <Download className="w-3 h-3" /> {a.name}
              </a>
            ))}
          </div>
        )}
        <div className="text-[10px] opacity-60 mt-1">
          {m.created_at ? new Date(m.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" }) : ""}
        </div>
      </div>
    </div>
  );
}
